/**
 * @name UploadFile
 * @description Componente común para añadir archivos adjuntos (Drag and Drop)
 * @author Ricardo Guerra
 */

import React, { Component } from 'react';
import i18n from 'i18next';
import PropTypes from 'prop-types';
import { createMuiTheme, ThemeProvider } from '@material-ui/core/styles';

// Material-UI Dropzone
import { DropzoneAreaBase } from 'material-ui-dropzone';

import { SnackbarService } from '@services';
import { Icons, Theme, Typography } from '@styles';

/*
AcceptedFormats
ZIP = application/zip
ZIP_COMPRESSED = application/x-zip-compressed
ZIP_STREAM = application/octet-stream
PDF = application/pdf
DOCX = application/msword
XLSX = application/vnd.ms-excel
DOCX_OFFICE = application/vnd.openxmlformats-officedocument.wordprocessingml.document
XLSX_OFFICE = application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
PPTX_OFFICE = application/vnd.openxmlformats-officedocument.presentationml.presentation
PNG = image/png
JPG = image/jpg
JPEG = image/jpeg
CSV = text/csv
JSON = text/json
JSON =  application/json
*/

class UploadFile extends Component {
  constructor(props) {
    super(props);

    // Obtiene el tamaño máximo configurado por el administrador si esta logueado con token si no obtiene uno por default (3 MB) o el asigando por el component UploadFile.
    this.enterpriseParams = JSON.parse(localStorage.getItem('ls.infoUsuario')) ? JSON.parse(localStorage.getItem('ls.infoUsuario')).Empresa : null;
    const maxSizeEnterprise = this.enterpriseParams ? this.enterpriseParams['TamañoArchivo'] : 3;
    const maxSizeMessage = props.maxSize ? props.maxSize : maxSizeEnterprise;
    const formatsAccepted = props.formatsAccepted;
    const formatsMessage = props.formatsMessage;

    this.state = {
      fileObjs: [],
      typeIcon: '',
      formats: formatsAccepted,
      maxSize: maxSizeMessage
    };

    this.dropzoneText = (
      <>
        {`${i18n.t('UploadFile.dragAndDrop')}${i18n.t('UploadFile.browseFile')}`}
        <br />
        <span>
          <Typography.Small>{`${i18n.t('UploadFile.files')}: ${formatsMessage}`}</Typography.Small>
          <br />
          <Typography.Medium>
            <Typography.Small>{`(${i18n.t('GenericMessages.max')} ${maxSizeMessage} MB)`}</Typography.Small>
          </Typography.Medium>
        </span>
      </>
    );
  }


  componentDidMount() {
    const { fileObject } = this.props;
    this.setState({ fileObjs: fileObject });
  }

  handleFileAdd = (file) => {
    const { type } = file[0].file;
    let typeIcon;

    if (type.startsWith('video/')) {
      typeIcon = <Icons.Theaters />;
    }

    if (type.startsWith('audio/')) {
      typeIcon = <Icons.AudioTrack />;
    }

    switch (type) {
      case 'application/msword':
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        typeIcon = <Icons.FileWord />;
        break;
      case 'application/vnd.ms-excel':
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        typeIcon = <Icons.FileExcel />;
        break;
      case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        typeIcon = <Icons.FilePowerpoint />;
        break;
      case 'application/pdf':
        typeIcon = <Icons.FilePDF />;
        break;
      default:
        typeIcon = <Icons.AttachFile />;
        break;

    }
    this.setState({ fileObjs: file, typeIcon });
    return this.props.onChangeFile(file);
  }

  handleFileDelete = () => {
    this.setState({ fileObjs: [] });
    return this.props.onChangeFile([]);
  }

  render() {
    const materialTheme = createMuiTheme({
      overrides: {
        MuiDropzoneArea: {
          root: {
            minHeight: '120px',
            borderColor: Theme.colors.greyMedium,
            '&:hover': {
              borderColor: this.state.fileObjs.length >= 1 ? Theme.colors.greyMedium : Theme.colors.greyLight,
            }
          },
          icon: {
            color: Theme.colors.greyDark,
            height: '40px',
            width: '40px',
            marginBottom: '8px',
            display: this.state.fileObjs.length >= 1 && 'none'
          },
          text: {
            padding: '8px',
            marginTop: '8px',
            marginBottom: '0px',
            fontFamily: 'Bryant Pro Regular',
            fontSize: Theme.typography.bodyText,
            fontWeight: 'normal',
            letterSpacing: 'normal',
            whiteSpace: 'normal',
          }
        },
        MuiDropzonePreviewList: {
          root: {
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '8px'
          },
          imageContainer: {
            padding: '8px !important',
            '& .MuiChip-root': {
              borderColor: Theme.colors.greyDark,
              color: Theme.colors.greyDark,
            },
            '& .MuiChip-icon': {
              color: Theme.colors.greyDark,
            },
            '& svg': {
              fontSize: '20px',
              height: '20px',
              width: '20px'
            }
          }
        }
      }
    });

    return (
      <ThemeProvider theme={materialTheme}>
        <DropzoneAreaBase
          acceptedFiles={this.state.formats}
          dropzoneText={this.dropzoneText}
          dropzoneProps={{
            disabled: Boolean(this.state.fileObjs.length >= 1),
            maxFiles: 1,
            multiple: false
          }}
          filesLimit={1}
          fileObjects={this.state.fileObjs}
          useChipsForPreview
          previewChipProps={{ icon: this.state.typeIcon }}
          maxFileSize={this.state.maxSize * 1048576}
          showAlerts={false}
          getDropRejectMessage={(rejectedFile, acceptedFiles, maxFileSize) => {
            let message = i18n.t('UploadFile.messageFileRejected', { fileName: rejectedFile.name });
            if (acceptedFiles.length > 0 && !acceptedFiles.find((format) => format === rejectedFile.type)) {
              message += i18n.t('UploadFile.messageInvalidType');
            }
            if (rejectedFile.size > maxFileSize) {
              message += i18n.t('UploadFile.messageFileTooBig', { maxSize: (maxFileSize / 1048576) });
            }
            SnackbarService.error(message);
          }}
          getFileAddedMessage={(fileName) => {
            let message = i18n.t('UploadFile.messageAddedFile', { fileName });
            SnackbarService.success(message);
          }}
          getFileRemovedMessage={(fileName) => {
            let message = i18n.t('UploadFile.messageRemovedFile', { fileName });
            SnackbarService.info(message);
          }}
          onAdd={(file) => {
            this.handleFileAdd(file);
          }}
          onDelete={(file) => {
            this.handleFileDelete(file);
          }}
        />
      </ThemeProvider>
    );
  }
}

UploadFile.propTypes = {
  // Objeto de tipo arreglo donde se guarda el archivo adjunto.
  fileObject: PropTypes.array,
  // Arreglo de formatos admitidos.
  formatsAccepted: PropTypes.array,
  // Mensaje de formatos admitidos.
  formatsMessage: PropTypes.string,
  // Tamaño máximo permitido del archivo adjunto.
  maxSize: PropTypes.number,
  // Función a ejecutar cuando se añade/elimina el archivo ajunto.
  onChangeFile: PropTypes.func
};

export default UploadFile;
